// Prevents DayPilot from re-rendering while the user holds the mouse button (drag/move/resize in progress).
// A re-render during a drag clears DayPilot's internal reference to the dragged event, which makes
// DayPilot's global mouseup handler throw "Cannot read properties of null (reading 'calendar')".
// Updates requested while the mouse is down are merged and applied once DayPilot has handled the mouseup.

let mouseDown = false;
let listenersAttached = false;
const flushers: Array<() => void> = [];

function flushAll(): void {
  flushers.slice().forEach(flush => flush());
}

function release(): void {
  // setTimeout lets DayPilot's own mouseup handler finish before any deferred update runs
  setTimeout(() => {
    mouseDown = false;
    flushAll();
  }, 0);
}

function attachListeners(): void {
  if (listenersAttached) return;
  listenersAttached = true;

  // capture phase, so the state is set before DayPilot handles the press
  document.addEventListener('mousedown', () => { mouseDown = true; }, true);
  document.addEventListener('mouseup', release, true);
  window.addEventListener('blur', release);
}

export function isSchedulerMouseDown(): boolean {
  return mouseDown;
}

// Wraps control.update so it is deferred while the mouse is down. Returns a function that removes the guard.
export function guardSchedulerUpdates(control: any): () => void {
  if (!control || control.__updateGuarded) return () => { };

  attachListeners();

  const originalUpdate = control.update;
  let pending = false;
  let pendingOptions: any = null;

  const callOriginal = (options?: any) => options ? originalUpdate.call(control, options) : originalUpdate.call(control);

  control.update = (options?: any) => {
    if (mouseDown) {
      pending = true;
      if (options) {
        pendingOptions = Object.assign(pendingOptions || {}, options);
      }
      return;
    }
    return callOriginal(options);
  };

  const flush = () => {
    if (!pending) return;
    const options = pendingOptions;
    pending = false;
    pendingOptions = null;
    try {
      callOriginal(options);
    } catch (error) {
      console.error("Deferred scheduler update failed", error);
    }
  };

  flushers.push(flush);
  control.__updateGuarded = true;

  return () => {
    const index = flushers.indexOf(flush);
    if (index !== -1) flushers.splice(index, 1);
    control.update = originalUpdate;
    control.__updateGuarded = false;
  };
}
